import { useRef, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';
import { setStepUpReason } from 'src/pages/admin/providers/stepUp';

import { OPERATIONS_NS as NS, OUTCOME_BUTTON_SX, TEXT_MAX } from './constants';
import { newRequestId, operationsErrorMessages } from './helpers';

export interface ConfirmField {
  name: string;
  label: string;
  hint?: string;
  multiline?: boolean;
  /** A note the BFF takes but does not require. */
  optional?: boolean;
  initial?: string;
  /** A pick from a list (a venue transfer), never typed (rule 2). */
  options?: { value: string; label: string }[];
  /** The i18n key of the problem with `value`, or undefined. */
  validate: (value: string, values: Record<string, string>) => string | undefined;
  /** A field shown only while others say so (the percent of "Part closed"). */
  visible?: (values: Record<string, string>) => boolean;
}

/**
 * Rule 2: every action confirms what money moves (or that none does), who approves next and what happens if nobody does.
 * The page builds one of these per action; the dialog only renders it and sends it once.
 */
export interface ConfirmSpec {
  title: string;
  /** What the operator is about to act on, above the three answers (the proposer's note, the figures, the progress strip). */
  summary?: React.ReactNode;
  moneyMoves: boolean;
  /** The money sentence(s): amount, from, to — or the sentence that no money moves. A function follows what is typed. */
  money: React.ReactNode | ((values: Record<string, string>) => React.ReactNode);
  approver: string;
  ifNobody: string;
  /** Present for a step-up action: shown here and on the authenticator prompt. */
  stepUpReason?: string;
  confirmLabel: string;
  confirmColor?: 'primary' | 'error';
  fields: ConfirmField[];
  /** Why the confirm button is disabled (nothing to send, a figure the BFF could not read). */
  blocked?: string | ((values: Record<string, string>) => string | undefined);
  /** `requestId` is the BFF's idempotency key: minted once per dialog and kept while the same body is retried. */
  submit: (values: Record<string, string>, requestId: string) => Promise<unknown>;
  doneMessage: string;
  /** Said after a success while the dialog stays open, for an action whose success is not the end (a propose that still needs a second operator). */
  outcome?: string;
}

export const ConfirmDialog = ({
  spec,
  onClose,
  onSettled,
}: {
  spec: ConfirmSpec;
  onClose: () => void;
  onSettled: () => Promise<unknown> | void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const smDown = useResponsive('down', 'sm');
  const t = (key: string, options?: object) => translate(`${NS}.confirm.${key}`, options);
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(spec.fields.map((f) => [f.name, f.initial ?? '']))
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<string | null>(null);
  const inFlight = useRef(false);
  const lastSent = useRef<{ key: string; body: string } | null>(null);

  const blockedNow = typeof spec.blocked === 'function' ? spec.blocked(values) : spec.blocked;

  const submit = async () => {
    if (inFlight.current || blockedNow) return;
    const invalid: Record<string, string> = {};
    spec.fields.forEach((f) => {
      if (f.visible && !f.visible(values)) return;
      const problem = f.validate(values[f.name] ?? '', values);
      if (problem) invalid[f.name] = problem;
    });
    setErrors(invalid);
    if (Object.keys(invalid).length > 0) return;
    const trimmed = Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v.trim()]));
    // One idempotency key per click; a retry of the same body resends it, an edited body gets a new one.
    const body = JSON.stringify(trimmed);
    const key = lastSent.current?.body === body ? lastSent.current.key : newRequestId();
    lastSent.current = { key, body };
    inFlight.current = true;
    setSubmitting(true);
    setAlert(null);
    if (spec.stepUpReason) setStepUpReason(spec.stepUpReason);
    try {
      await spec.submit(trimmed, key);
      notify(spec.doneMessage, { type: 'success' });
      if (spec.outcome) setOutcome(spec.outcome);
      else onClose();
    } catch (error) {
      setAlert(operationsErrorMessages(error, translate));
    } finally {
      setStepUpReason(null);
      inFlight.current = false;
      setSubmitting(false);
      await onSettled();
    }
  };

  const section = (label: string, body: React.ReactNode, testId: string) => (
    <Stack spacing={0.25} data-testid={testId}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ overflowWrap: 'anywhere' }}>
        {body}
      </Typography>
    </Stack>
  );

  return (
    <Dialog
      open
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={smDown}
    >
      <DialogTitle>{spec.title}</DialogTitle>
      <DialogContent>
        {outcome && (
          <Alert severity="warning" sx={{ mt: 1 }} data-testid="confirm-outcome">
            {outcome}
          </Alert>
        )}
        {!outcome && (
        <Stack spacing={2} sx={{ mt: 1 }}>
          {spec.summary}
          {section(
            t(spec.moneyMoves ? 'moneyMoves' : 'noMoney'),
            typeof spec.money === 'function' ? spec.money(values) : spec.money,
            'confirm-money'
          )}
          {section(t('approver'), spec.approver, 'confirm-approver')}
          {section(t('ifNobody'), spec.ifNobody, 'confirm-if-nobody')}
          {spec.stepUpReason && (
            <Alert severity="info" data-testid="confirm-step-up">
              {t('stepUp', { reason: spec.stepUpReason })}
            </Alert>
          )}
          {blockedNow && <Alert severity="warning">{blockedNow}</Alert>}
          {alert && (
            <Alert severity="error">
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                {alert.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </Stack>
            </Alert>
          )}
          {spec.fields
            .filter((f) => !f.visible || f.visible(values))
            .map((f) => (
              <TextField
                key={f.name}
                required={!f.optional}
                fullWidth
                select={Boolean(f.options)}
                multiline={f.multiline}
                minRows={f.multiline ? 2 : undefined}
                label={f.label}
                value={values[f.name] ?? ''}
                onChange={(e) => setValues((cur) => ({ ...cur, [f.name]: e.target.value }))}
                error={Boolean(errors[f.name])}
                helperText={errors[f.name] ? translate(errors[f.name], { max: TEXT_MAX }) : f.hint}
                disabled={submitting}
                inputProps={f.options ? undefined : { maxLength: TEXT_MAX + 100 }}
              >
                {f.options?.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            ))}
        </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button sx={OUTCOME_BUTTON_SX} onClick={onClose} disabled={submitting}>
          {t(outcome ? 'close' : 'back')}
        </Button>
        {!outcome && (
          <LoadingButton
            sx={OUTCOME_BUTTON_SX}
            variant="contained"
            color={spec.confirmColor ?? 'primary'}
            loading={submitting}
            disabled={Boolean(blockedNow)}
            onClick={submit}
          >
            {alert ? t('retry') : spec.confirmLabel}
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};
